import React from 'react'
import { Chip, useTheme } from '@mui/material'
import { Theme } from '@mui/material/styles'
import {
    Check,
    Schedule,
    Warning,
    Info,
    HourglassEmpty,
    Block,
} from '@mui/icons-material'

export type InspectionChipStatus =
    | 'complete'
    | 'action_required'
    | 'ready'
    | 'overdue'
    | 'upcoming'
    | 'pending'
    | 'processing'
    | 'not_available'

interface InspectionStatusChipProps {
    status: InspectionChipStatus
    label?: string
}

const STATUS_CONFIG: Record<
    InspectionChipStatus,
    {
        label: string
        color: 'success' | 'warning' | 'error' | 'info' | 'default'
        icon: React.ReactElement
    }
> = {
    complete: { label: 'Complete', color: 'success', icon: <Check /> },
    action_required: {
        label: 'Action Required',
        color: 'warning',
        icon: <Schedule />,
    },
    ready: { label: 'Ready', color: 'warning', icon: <Schedule /> },
    overdue: { label: 'Overdue', color: 'error', icon: <Warning /> },
    upcoming: { label: 'Upcoming', color: 'warning', icon: <Schedule /> },
    pending: { label: 'In Progress', color: 'info', icon: <HourglassEmpty /> },
    processing: {
        label: 'In Progress',
        color: 'info',
        icon: <HourglassEmpty />,
    },
    not_available: {
        label: 'Not Available',
        color: 'default',
        icon: <Block />,
    },
}

export const InspectionStatusChip: React.FC<InspectionStatusChipProps> = ({
    status,
    label,
}) => {
    const theme = useTheme()
    const config = STATUS_CONFIG[status]
    const displayLabel = label ?? config.label

    if (status === 'not_available') {
        return (
            <Chip
                icon={config.icon}
                label={displayLabel}
                size="small"
                sx={{
                    fontWeight: 600,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.5)',
                    '& .MuiChip-icon': { color: 'rgba(255,255,255,0.4)' },
                }}
            />
        )
    }

    const paletteColor =
        config.color === 'default'
            ? theme.palette.grey[500]
            : theme.palette[config.color].main

    return (
        <Chip
            icon={config.icon}
            label={displayLabel}
            size="small"
            sx={{
                fontWeight: 600,
                bgcolor: paletteColor,
                color: '#fff',
                '& .MuiChip-icon': { color: '#fff' },
            }}
        />
    )
}

export const getStatusAccentColor = (
    status: InspectionChipStatus,
    theme: Theme,
): string => {
    switch (status) {
        case 'complete':
            return theme.palette.success.main
        case 'overdue':
            return theme.palette.error.main
        case 'action_required':
        case 'ready':
        case 'upcoming':
            return theme.palette.warning.main
        case 'pending':
        case 'processing':
            return theme.palette.info.main
        default:
            return theme.palette.grey[600]
    }
}
